import { useEffect, useRef, useState } from 'react';
import { nativeRequest } from './nativeBridge';

type Preview = { recipient_count: number; estimated_coins: number; dispatch: { dispatch_id: number } | null };
export function BroadcastDialog({ title, children, onClose }: { title: string; children: React.ReactNode; onClose?: () => void }) {
  const dialog = useRef<HTMLDivElement>(null);
  const close = useRef(onClose); close.current = onClose;
  useEffect(() => {
    const previous = document.activeElement as HTMLElement;
    dialog.current?.focus();
    return () => previous?.focus();
  }, []);
  return <div className="studio-modal" onKeyDown={event => {
    if (event.key === 'Escape') { event.preventDefault(); close.current?.(); }
    if (event.key !== 'Tab') return;
    const controls = dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]');
    if (!controls?.length) { event.preventDefault(); return; }
    const first = controls[0], last = controls[controls.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }}><div className="studio-modal__sheet" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={dialog}>
    <h2>{title}</h2>{children}
  </div></div>;
}

export default function BroadcastNotification({ onDismiss, onSent }: { onDismiss: () => void; onSent: () => void }) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [sending, setSending] = useState(false);
  const pending = useRef(false);
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  useEffect(() => {
    let current = true;
    setPreview(null); setError('');
    nativeRequest<Preview>('notification-preview').then(data => {
      if (!current) return;
      if (Number.isSafeInteger(data?.dispatch?.dispatch_id)) { onDismiss(); return; }
      if (!Number.isSafeInteger(data?.recipient_count) || data.recipient_count < 0 || !Number.isSafeInteger(data?.estimated_coins) || data.estimated_coins < 0) throw new Error('Invalid preview');
      setPreview(data);
    }).catch(() => { if (current) setError('발송 정보를 불러오지 못했습니다. 다시 확인해주세요.'); });
    return () => { current = false; };
  }, [retry, onDismiss]);
  const send = async () => {
    if (pending.current || !preview || preview.recipient_count === 0) return;
    pending.current = true; setSending(true); setError('');
    try {
      const data = await nativeRequest<{ dispatch_id: number }>('notification-send');
      if (!Number.isSafeInteger(data?.dispatch_id)) throw new Error('Unknown dispatch');
      if (active.current) onSent();
    } catch {
      try {
        const data = await nativeRequest<Preview>('notification-preview');
        if (Number.isSafeInteger(data?.dispatch?.dispatch_id)) { if (active.current) onSent(); return; }
      } catch { /* The server also deduplicates dispatches. */ }
      if (active.current) setError('발송 결과를 확인하지 못했습니다. 관리자 화면에서 발송 내역을 확인해주세요.');
    } finally { pending.current = false; if (active.current) setSending(false); }
  };
  return <BroadcastDialog title="라이브 시작 알림톡을 보내시겠습니까?" onClose={sending ? undefined : onDismiss}>
    <p>시작 알림톡은 이 라이브에서 한 번만 보낼 수 있습니다.</p>
    <p>나중에 보내려면 관리자 화면에서 직접 발송할 수 있습니다.</p>
    {preview ? <p>대상 {preview.recipient_count.toLocaleString()}명 · 예상 사용 {preview.estimated_coins.toLocaleString()}코인</p> : !error && <p>발송 정보를 확인하고 있습니다.</p>}
    {preview?.recipient_count === 0 && <p>현재 알림톡을 받을 대상이 없습니다.</p>}
    {error && <p role="alert">{error}</p>}
    {error && !preview && <button type="button" onClick={() => setRetry(value => value + 1)}>다시 확인</button>}
    <div className="studio-modal__actions">
      <button type="button" disabled={sending} onClick={onDismiss}>나중에 보내기</button>
      <button type="button" className="primary" disabled={sending || !preview || preview.recipient_count === 0} onClick={send}>{sending ? '보내는 중…' : '지금 보내기'}</button>
    </div>
  </BroadcastDialog>;
}
